import type { Exercise } from '@/Common/ActivityFunctions';
import { writable } from 'svelte/store';
import { DataStatus } from './common';
import { getRequest } from '@/Util';
import { debouncer } from '@/Util/utils';

/*
This singleton loads and manages the activities for a user for a specific date.
- Object must be initialized once in an onMount function for reactivity to work, preferably in a parent component.
*/

export interface ActivitiesForDateWritable {
  status: DataStatus;
  date: Date;
  activities: Exercise[];
}

export let hasSubscribers = false;

let currentDate: Date = null;
let loadCounter: number = 0;

function createSingleton() {
  const { subscribe, set, update } = writable<ActivitiesForDateWritable>({
    status: DataStatus.NEW,
    date: new Date(),
    activities: [],
  }, () => {
    hasSubscribers = true;
    return () => {
      hasSubscribers = false;
    };
  });
  
  async function load(date: Date, loadNumber: number): Promise<void> {
    // If a newer load has been initiated, discard this one.
    if (loadNumber != loadCounter) {
      return;
    }

    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
  
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
  
    const activities = await window
      .fetch(
        '/calendar/events?' +
          new URLSearchParams({
            start: start.toISOString(),
            end: end.toISOString(),
            includeMatched: 'true',
            includeDuplicates: 'true',
            includeHidden: 'true',
          }),
        {
          method: 'GET',
          ...getRequest(),
        },
      )
      .then((r) => r.json());
  
    // If a newer load has been initiated, discard this one.
    if (loadNumber != loadCounter) {
      return;
    }

    console.log('Singleton: Loaded activities for date', date, activities);
    set({
      status: DataStatus.READY,
      date: date,
      activities: activities,
    });
  }

  const loadDebouncer = debouncer(200);
  const executeLoadDebouncer = (date, loadNumber) => loadDebouncer.debounce(() => load(date, loadNumber));

  async function reactiveReload(data) {
    if (data.refresh_date && data.refresh_date != '') {
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (new Date(data.refresh_date) <= endOfDay) {
        update((prev) => {
          return {
            status: prev.status == DataStatus.LOADING ? DataStatus.LOADING : DataStatus.RESYNCING,
            date: prev.date,
            activities: prev.activities,
          };
        });

        executeLoadDebouncer(currentDate, ++loadCounter);
      }
    }
  }

  async function init(date: Date) {
    let isNew = false;
    currentDate = date;
    update((prev) => {
      if (prev.status !== DataStatus.NEW) {
        return prev;
      }
      isNew = true;
      return {
        status: DataStatus.LOADING,
        date: date,
        activities: [],
      };
    });
    // Only initialize once.
    if (!isNew) return;

    load(date, ++loadCounter);

    //@ts-ignore
    const Xert = window.Xert;
    Xert._userChannel.bind('schedule_activity_complete', reactiveReload);
    Xert._userChannel.bind('modify_activity_complete', reactiveReload);
    Xert._userChannel.bind('delete_activity_complete', reactiveReload);
    Xert._userChannel.bind('upload_activity_complete', reactiveReload);
    Xert._userChannel.bind('trickle_complete', reactiveReload);
    Xert._userChannel.bind('sessions', reactiveReload);
    Xert._userChannel.bind('group_activity_update', reactiveReload);
  }

  function changeDate(date: Date) {
    currentDate = date;
    update((prev) => {
      return {
        status: DataStatus.LOADING,
        date: date,
        activities: prev.activities,
      };
    });

    load(date, ++loadCounter);
  }

  return {
    subscribe,
    set,
    update,
    init,
    changeDate,
  };
}

export const activitiesForDate = createSingleton();
